'use client';

import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { useTheme } from '@/providers/theme-provider';
import { cn } from '@/lib/utils';
import type { ThemeMode, ThemeName } from '@/lib/themes';

const MODE_OPTIONS: { value: ThemeMode; label: string; icon: React.ElementType }[] = [
  { value: 'light', label: 'Acik', icon: Sun },
  { value: 'dark', label: 'Koyu', icon: Moon },
  { value: 'system', label: 'Sistem', icon: Monitor },
];

const THEME_PREVIEW_COLORS: Record<ThemeName, string> = {
  emerald: '#10B981',
  ocean: '#3B82F6',
  sunset: '#F97316',
  lavender: '#8B5CF6',
  monochrome: '#71717A',
};

export function ThemeSelector() {
  const { theme, mode, setTheme, setMode, themes } = useTheme();

  return (
    <div className="flex flex-col gap-6">
      {/* Mode Selection */}
      <div>
        <span id="mode-label" className="text-sm font-medium text-muted-foreground mb-3 block">Görünüm Modu</span>
        <div className="flex gap-2" role="group" aria-labelledby="mode-label">
          {MODE_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isActive = mode === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setMode(opt.value)}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all',
                  isActive
                    ? 'bg-primary/10 text-primary ring-1 ring-primary/30'
                    : 'bg-secondary text-muted-foreground hover:bg-secondary/80 hover:text-foreground'
                )}
              >
                <Icon className="size-4" />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Theme Selection */}
      <div>
        <label className="text-sm font-medium text-muted-foreground mb-3 block">Renk Temasi</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {themes.map((t) => {
            const isActive = theme === t.name;
            const previewColor = THEME_PREVIEW_COLORS[t.name];
            return (
              <button
                key={t.name}
                onClick={() => setTheme(t.name)}
                className={cn(
                  'relative flex items-start gap-3 p-4 rounded-xl text-left transition-all',
                  isActive
                    ? 'bg-primary/10 ring-1 ring-primary/30'
                    : 'bg-secondary hover:bg-secondary/80'
                )}
              >
                {/* Color Preview */}
                <div
                  className="size-10 rounded-lg shrink-0"
                  style={{ 
                    background: `linear-gradient(135deg, ${previewColor} 0%, ${previewColor}99 100%)`,
                    boxShadow: isActive ? `0 0 12px ${previewColor}40` : 'none'
                  }}
                />
                
                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      'font-medium text-sm',
                      isActive ? 'text-primary' : 'text-foreground'
                    )}>
                      {t.label}
                    </span>
                    {isActive && (
                      <Check className="size-4 text-primary" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                    {t.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Preview */}
      <div>
        <label className="text-sm font-medium text-muted-foreground mb-3 block">Önizleme</label>
        <div className="flex flex-col rounded-xl border border-border/50 p-4 gap-3">
          {/* Sample buttons */}
          <div className="flex gap-2">
            <button className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">
              Birincil Buton
            </button>
            <button className="px-4 py-2 rounded-lg bg-secondary text-secondary-foreground text-sm font-medium">
              Ikincil Buton
            </button>
          </div>
          
          {/* Sample metrics */}
          <div className="grid grid-cols-4 gap-2">
            <div className="p-3 rounded-lg bg-card border border-border/50">
              <div className="text-xs text-muted-foreground">Sıcaklık</div>
              <div className="text-lg font-semibold" style={{ color: 'var(--chart-1)' }}>24.5C</div>
            </div>
            <div className="p-3 rounded-lg bg-card border border-border/50">
              <div className="text-xs text-muted-foreground">Nem</div>
              <div className="text-lg font-semibold" style={{ color: 'var(--chart-2)' }}>65%</div>
            </div>
            <div className="p-3 rounded-lg bg-card border border-border/50">
              <div className="text-xs text-muted-foreground">Isik</div>
              <div className="text-lg font-semibold" style={{ color: 'var(--chart-3)' }}>850</div>
            </div>
            <div className="p-3 rounded-lg bg-card border border-border/50">
              <div className="text-xs text-muted-foreground">Toprak</div>
              <div className="text-lg font-semibold" style={{ color: 'var(--chart-5)' }}>42%</div>
            </div>
          </div>

          {/* Sample status indicators */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-muted-foreground">Aktif</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: 'var(--warning)' }} />
              <span className="text-muted-foreground">Uyarı</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: 'var(--destructive)' }} />
              <span className="text-muted-foreground">Kritik</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact theme toggle for navbar
 */
export function ThemeToggle() {
  const { mode, setMode, resolvedMode } = useTheme();

  const toggleMode = () => {
    if (mode === 'dark') {
      setMode('light');
    } else if (mode === 'light') {
      setMode('system');
    } else {
      setMode('dark');
    }
  };

  const Icon = mode === 'system' ? Monitor : resolvedMode === 'dark' ? Moon : Sun;

  return (
    <button
      onClick={toggleMode}
      className="flex items-center justify-center min-w-11 min-h-11 rounded-lg text-zinc-500 hover:text-foreground hover:bg-accent transition-all"
      title={`Mod: ${mode === 'dark' ? 'Koyu' : mode === 'light' ? 'Acik' : 'Sistem'}`}
    >
      <Icon className="size-5" />
    </button>
  );
}
